import * as React from "react";
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner";

export type ToastStatus = "success" | "error" | "warning" | "info";

export interface ToastOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  status?: ToastStatus;
  duration?: number | null;
  isClosable?: boolean;
  onCloseComplete?: () => void;
}

export type ToastFn = (options: ToastOptions) => string | number;

/**
 * Custom-Status-Icon: farbiger Kreis mit weißem Glyph statt Sonner-Standard.
 * Farbsemantik: success = Teal, error = Protein/Rot, warning = Gold, info = Kcal/Blau.
 */
function StatusIcon({
  color,
  children,
}: {
  color: "text-teal" | "text-protein" | "text-gold" | "text-blue";
  children: React.ReactNode;
}) {
  return (
    <svg
      aria-hidden
      width={20}
      height={20}
      viewBox="0 0 20 20"
      className={color}
    >
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      {children}
    </svg>
  );
}

function SuccessIcon() {
  return (
    <StatusIcon color="text-teal">
      <path
        d="M5.5 10.5l2.8 2.8 6.2-6.2"
        fill="none"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StatusIcon>
  );
}

function ErrorIcon() {
  return (
    <StatusIcon color="text-protein">
      <path
        d="M10 5.5v5.5"
        fill="none"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <circle cx="10" cy="14.3" r="1.2" fill="white" />
    </StatusIcon>
  );
}

function WarningIcon() {
  return (
    <StatusIcon color="text-gold">
      <path
        d="M10 5.5v5.5"
        fill="none"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <circle cx="10" cy="14.3" r="1.2" fill="white" />
    </StatusIcon>
  );
}

function InfoIcon() {
  return (
    <StatusIcon color="text-blue">
      <path
        d="M10 9.3v5.2"
        fill="none"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <circle cx="10" cy="6.3" r="1.2" fill="white" />
    </StatusIcon>
  );
}

/**
 * Close-Icon mit `pointer-events: none`. Sonst trifft der Klick das SVG statt
 * den Button; Sonner startet dann seine Swipe-/Pointer-Capture-Logik und der
 * Klick geht auf das darunterliegende Element über.
 */
function CloseIcon() {
  return (
    <svg
      aria-hidden
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ pointerEvents: "none" }}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export const ToastProvider = () => (
  <SonnerToaster
    position="bottom-center"
    theme="system"
    closeButton
    gap={8}
    offset={16}
    mobileOffset={{ left: 28, right: 28 }}
    icons={{
      success: <SuccessIcon />,
      error: <ErrorIcon />,
      warning: <WarningIcon />,
      info: <InfoIcon />,
      close: <CloseIcon />,
    }}
    style={
      {
        "--normal-bg": "var(--p2p-card)",
        "--normal-border": "var(--p2p-border)",
        "--normal-text": "var(--p2p-text)",
        "--border-radius": "14px",
        "--width": "320px",
      } as React.CSSProperties
    }
    toastOptions={{
      duration: 4000,
      className: "font-sans",
      classNames: {
        toast: "!shadow-card px-4 py-3 !pointer-events-auto",
        title: "!font-bold",
        description: "!text-ink-muted text-sm",
        // Close-Button: ohne Kreis, per Flex-Order rechts in der Toast-Zeile.
        closeButton:
          "!static !transform-none !order-last !h-6 !w-6 !bg-transparent !border-0 !text-ink-muted !pointer-events-auto",
      },
    }}
  />
);

export const useToast = (): ToastFn =>
  React.useCallback((options: ToastOptions): string | number => {
    const {
      title,
      description,
      status,
      duration,
      isClosable,
      onCloseComplete,
    } = options;
    const data: Parameters<typeof sonnerToast>[1] = {
      description,
      closeButton: isClosable,
      onDismiss: onCloseComplete,
    };
    if (duration === null) {
      data.duration = Infinity;
    } else if (duration !== undefined) {
      data.duration = duration;
    }
    switch (status) {
      case "success":
        return sonnerToast.success(title, data);
      case "error":
        return sonnerToast.error(title, data);
      case "warning":
        return sonnerToast.warning(title, data);
      case "info":
        return sonnerToast.info(title, data);
      default:
        return sonnerToast(title, data);
    }
  }, []);
